import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Download, Link2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ShareCardDialog({ open, onOpenChange, person, problem }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!person || !problem) return null;

  const shareUrl = `${window.location.origin}/people/${person.id}`;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `solvers-${person.id}-verified.png`;
      a.click();
      toast.success("Share card downloaded.");
    } catch (e) {
      toast.error("Couldn't render the image — try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — copy manually.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white p-0 overflow-hidden rounded-3xl">
        <div className="p-6 pb-2">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl text-[#111827]">
              Share this verified solution
            </DialogTitle>
            <DialogDescription className="text-[#4B5563] font-body">
              A ready-to-post card for social. Every verified solution helps
              others find {person.name.split(" ")[0]}.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* The share card (rendered to PNG). Fixed aspect for OG-like output. */}
        <div className="px-6">
          <div
            ref={cardRef}
            data-testid="share-card"
            className="relative w-full aspect-[1200/630] rounded-2xl bg-[#EBFFF5] overflow-hidden"
            style={{
              backgroundImage:
                "radial-gradient(1200px 400px at 100% 0%, #D6FFEB 0%, #EBFFF5 60%, #FAFAFA 100%)",
            }}
          >
            <div className="absolute inset-0 flex flex-col p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-xl bg-[#3FFFB6] flex items-center justify-center text-[#111827]">
                    <Sparkles size={14} strokeWidth={2.4} />
                  </span>
                  <span className="font-display font-bold text-lg tracking-tight text-[#111827]">
                    Oiiister
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#065F46] bg-[#ECFDF5] px-3 py-1.5 rounded-full ring-1 ring-[#D1FAE5]">
                  <BadgeCheck size={12} />
                  Solution
                </span>
              </div>

              <div className="mt-auto">
                <p className="font-display font-bold text-[#111827] leading-tight text-2xl sm:text-3xl">
                  {problem.title}
                </p>
                {problem.outcome && (
                  <p className="mt-3 font-body text-[#111827] text-sm sm:text-base leading-relaxed border-l-2 border-[#3FFFB6] pl-3 max-w-[85%]">
                    {problem.outcome}
                  </p>
                )}

                <div className="mt-5 flex items-center gap-3">
                  <img
                    src={person.photo}
                    alt=""
                    crossOrigin="anonymous"
                    className="w-11 h-11 rounded-full object-cover ring-2 ring-white"
                  />
                  <div>
                    <p className="font-display font-semibold text-[#111827] leading-tight text-sm">
                      {person.name}
                    </p>
                    <p className="text-xs text-[#4B5563]">{person.role}</p>
                  </div>
                  <div className="ml-auto flex flex-wrap gap-1.5 justify-end">
                    {(problem.tools || []).slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white ring-1 ring-[#F3F4F6] text-[#4B5563]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 pt-4 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleDownload}
            disabled={busy}
            data-testid="share-download"
            className="flex-1 h-11 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold"
          >
            <Download size={16} className="mr-2" />
            {busy ? "Rendering…" : "Download PNG"}
          </Button>
          <Button
            onClick={handleCopy}
            variant="outline"
            data-testid="share-copy"
            className="flex-1 h-11 rounded-full bg-white ring-1 ring-[#E5E7EB] text-[#111827] font-semibold"
          >
            <Link2 size={16} className="mr-2" />
            Copy link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
